import React,{useState} from 'react';
import SafeIcon from '../../common/SafeIcon';
import {DOCUMENT_TYPES} from '../../config/documentTypes';
import SmartDateInput from '../ui/SmartDateInput';

const ALLOWED_EXTENSIONS=['pdf','docx','zip','png','jpg','jpeg'];
const ALLOWED_MIMES=[
'application/pdf',
'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
'application/zip',
'application/x-zip-compressed',
'multipart/x-zip',
'image/png',
'image/jpeg',
'image/jpg'
];
const MAX_SIZE=15 * 1024 * 1024;

const generateTempId=()=> {
if (typeof crypto !=='undefined' && crypto.randomUUID) {
return crypto.randomUUID();
}
return `temp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
};

const formatFileSize=(bytes)=> {
if (!bytes) return '0 Bytes';
const k=1024;
const sizes=['Bytes','KB','MB','GB'];
const i=Math.floor(Math.log(bytes) / Math.log(k));
return `${parseFloat((bytes / Math.pow(k,i)).toFixed(2))} ${sizes[i]}`;
};

const TenantCreationDocuments=({documents=[],onChange,disabled=false})=> {
const [title,setTitle]=useState('');
const [documentType,setDocumentType]=useState('lease_agreement');
const [documentDate,setDocumentDate]=useState(new Date().toISOString().split('T')[0]);
const [notes,setNotes]=useState('');
const [file,setFile]=useState(null);
const [error,setError]=useState('');

const handleFileSelect=(e)=> {
const selectedFile=e.target.files?.[0];
if (!selectedFile) return;

const extension=String(selectedFile.name || '').split('.').pop()?.toLowerCase();
const isAllowed=ALLOWED_MIMES.includes(selectedFile.type) || ALLOWED_EXTENSIONS.includes(extension) || (selectedFile.type==='' && ALLOWED_EXTENSIONS.includes(extension));
const isSizeOk=selectedFile.size <=MAX_SIZE;

if (!isAllowed) {
setError('Only PDF,DOCX,ZIP,PNG,JPG or JPEG files are allowed.');
e.target.value='';
return;
}

if (!isSizeOk) {
setError('File exceeds the 15MB size limit.');
e.target.value='';
return;
}

setError('');
setFile(selectedFile);
if (!title) {
const baseName=selectedFile.name.replace(/\.[^/.]+$/,'');
setTitle(baseName);
}
};

const handleAddDocument=()=> {
if (!file) {
setError('Please select a file to stage.');
return;
}
if (!title.trim()) {
setError('Document title is required.');
return;
}

const newDoc={
tempId: generateTempId(),
title: title.trim(),
documentType,
documentDate,
notes,
file
};

onChange([...documents,newDoc]);

setTitle('');
setDocumentType('lease_agreement');
setDocumentDate(new Date().toISOString().split('T')[0]);
setNotes('');
setFile(null);
setError('');
};

const handleRemoveDocument=(tempId)=> {
onChange(documents.filter(doc=> doc.tempId !==tempId));
};

const inputClass='w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white disabled:bg-slate-100 disabled:cursor-not-allowed';
const labelClass='block text-xs font-medium text-slate-600 mb-1';

return (
<div className="space-y-6">
<div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-2">
<SafeIcon icon="Info" className="text-amber-600 text-lg shrink-0 mt-0.5" />
<p className="text-sm text-amber-800">
Files are staged only. They will be uploaded and linked after the tenant and lease are saved.
</p>
</div>

<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
<div className="md:col-span-2">
<label className={labelClass}>Document Title</label>
<input
type="text"
value={title}
onChange={(e)=> setTitle(e.target.value)}
className={inputClass}
placeholder="e.g. Tenancy Agreement 2024"
disabled={disabled}
/>
</div>
<div>
<label className={labelClass}>Document Type</label>
<select
value={documentType}
onChange={(e)=> setDocumentType(e.target.value)}
className={inputClass}
disabled={disabled}
>
{DOCUMENT_TYPES.map((type)=> (
<option key={type.value} value={type.value}>
{type.label}
</option>
))}
</select>
</div>
<div>
<label className={labelClass}>Document Date</label>
<SmartDateInput
value={documentDate}
onChange={(e)=> setDocumentDate(e.target.value)}
className={inputClass}
disabled={disabled}
/>
</div>
<div className="md:col-span-2">
<label className={labelClass}>Notes</label>
<textarea
value={notes}
onChange={(e)=> setNotes(e.target.value)}
className={inputClass}
rows="2"
placeholder="Additional notes..."
disabled={disabled}
/>
</div>
<div className="md:col-span-2">
<label className={labelClass}>File</label>
<div className="relative border-2 border-dashed border-slate-200 rounded-xl p-6 bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer flex flex-col items-center justify-center text-center">
<input
type="file"
accept=".pdf,.docx,.zip,.png,.jpg,.jpeg,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/zip,application/x-zip-compressed,image/png,image/jpeg"
onChange={handleFileSelect}
className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
disabled={disabled}
/>
{file ? (
<div className="text-sm text-slate-700 font-medium">
{file.name} <span className="text-slate-500">({formatFileSize(file.size)})</span>
</div>
) : (
<>
<SafeIcon icon="UploadCloud" className="text-slate-400 text-xl mb-2" />
<p className="text-xs font-medium text-slate-700">Click to select a file</p>
<p className="text-[10px] text-slate-400 mt-1">PDF,DOCX,ZIP,PNG,JPG (max 15MB)</p>
</>
)}
</div>
</div>
</div>

{error && (
<div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-2">
{error}
</div>
)}

<button
type="button"
onClick={handleAddDocument}
disabled={disabled || !file || !title.trim()}
className="px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50 flex items-center gap-2"
>
<SafeIcon icon="Plus" className="text-sm" />
Add to Tenant
</button>

{documents.length > 0 && (
<div className="border-t border-slate-200 pt-4">
<h4 className="text-sm font-semibold text-slate-800 mb-3">Staged Documents ({documents.length})</h4>
<div className="space-y-2">
{documents.map((doc)=> (
<div key={doc.tempId} className="flex items-center justify-between bg-white border border-slate-200 px-3 py-2 rounded-lg">
<div className="flex items-center gap-3 min-w-0">
<SafeIcon icon="FileText" className="text-slate-400 shrink-0" />
<div className="truncate">
<p className="text-sm font-medium text-slate-800 truncate">{doc.title}</p>
<p className="text-xs text-slate-500">
{DOCUMENT_TYPES.find(t=> t.value===doc.documentType)?.label || doc.documentType} • {doc.file?.name} • {formatFileSize(doc.file?.size)}
</p>
</div>
</div>
<button
type="button"
onClick={()=> handleRemoveDocument(doc.tempId)}
disabled={disabled}
className="text-red-500 hover:bg-red-50 p-1 rounded transition-colors shrink-0 disabled:opacity-50"
>
<SafeIcon icon="X" className="text-sm" />
</button>
</div>
))}
</div>
</div>
)}
</div>
);
};

export default TenantCreationDocuments;